import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../components/SeoHead";
import GeneralContactForm from "../components/GeneralContactForm";
import { BUSINESS_INFO, IMAGES, PRODUCTS_AND_EQUIPMENT } from "../data";

export default function HusLejlighedRengoering() {
  return (
    <>
      <SeoHead
        title="Rengøring af hus eller lejlighed"
        description="Grundig og pålidelig privat rengøring af hus og lejlighed i Norddjurs. Fleksible aftaler, miljøvenlige produkter og et skinnende rent hjem."
        path="/rengoering-af-hus-eller-lejlighed"
      />

      {/* SUBPAGE HERO */}
      <section className="bg-gradient-to-b from-[#F4EFE6] to-[#FAF7F2] py-12 lg:py-20 border-b border-[#EADFCF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            <div className="lg:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 bg-[#EADFCF] text-[#4B3C2D] px-3.5 py-1 rounded-full text-xs font-semibold">
                <i className="fa-solid fa-house-user text-[#887055]" aria-hidden="true"></i>
                <span>Privat rengøring i Norddjurs</span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-heading text-[#2C2523] tracking-tight">
                Rengøring af hus eller lejlighed
              </h1>

              <p className="text-base text-[#5C524C] leading-relaxed max-w-2xl">
                Nyd et skinnende rent og indbydende hjem uden stress i hverdagen. Hos Ses Multi Service tilbyder vi professionel og grundig rengøring af både huse og lejligheder i hele Norddjurs.
              </p>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <a
                  href={`tel:${BUSINESS_INFO.phone}`}
                  className="bg-[#3A302B] hover:bg-[#231C19] text-[#FFFDF9] px-6 py-3 rounded-xl font-semibold text-sm transition-colors flex items-center gap-2 shadow-xs active:scale-[0.98] whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
                  <span className="whitespace-nowrap">Ring 40 94 75 40</span>
                </a>
                <a
                  href="#bestil-form"
                  className="bg-[#EADFCF] hover:bg-[#D8C7B0] text-[#2C2523] px-6 py-3 rounded-xl font-semibold text-sm transition-colors border border-[#D8C7B0]"
                >
                  Bestil rengøring
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden border border-[#EADFCF] shadow-lg relative group">
                <img
                  src={IMAGES.houseApartment}
                  alt="Professionel rengøring af hus eller lejlighed i Norddjurs"
                  className="w-full h-[340px] sm:h-[400px] object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-5">
                  <p className="text-white text-xs sm:text-sm font-medium flex items-center gap-2">
                    <i className="fa-solid fa-sparkles text-[#EADFCF]" aria-hidden="true"></i>
                    Skinnende rene resultater med miljøvenlige midler
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="py-14 lg:py-20 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="max-w-3xl space-y-3">
            <span className="text-xs font-bold uppercase tracking-wider text-[#887055]">
              Løsninger til dit hjem
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold font-heading text-[#2C2523]">
              Hvilken type rengøring har du brug for?
            </h2>
            <p className="text-sm sm:text-base text-[#5C524C] leading-relaxed">
              Vi tilpasser altid rengøringen efter dine konkrete ønsker, boligens størrelse og dine hverdagsrutiner. Uanset om du ønsker faste besøg eller en engangsløsning, står vi klar.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            <div className="bg-[#FFFDF9] rounded-2xl border border-[#EADFCF] p-6 space-y-4 shadow-2xs hover:border-[#887055] transition-colors">
              <div className="w-12 h-12 rounded-xl bg-[#EADFCF] text-[#6B5640] flex items-center justify-center text-xl">
                <i className="fa-solid fa-calendar-check" aria-hidden="true"></i>
              </div>
              <h3 className="text-xl font-bold font-heading text-[#2C2523]">
                Fast rengøringsaftale
              </h3>
              <p className="text-xs sm:text-sm text-[#5C524C] leading-relaxed">
                Regelmæssig rengøring hver uge eller hver 14. dag. Du slipper for hverdagsrengøringen og kommer altid hjem til et rent, duftende og indbydende hjem.
              </p>
              <ul className="text-xs text-[#2C2523] space-y-2 pt-2 border-t border-[#EADFCF]">
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#887055]"></i>
                  <span>Fast, tryg kontaktperson</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#887055]"></i>
                  <span>Faste ugedage og tidsrum</span>
                </li>
              </ul>
            </div>

            <div className="bg-[#FFFDF9] rounded-2xl border border-[#EADFCF] p-6 space-y-4 shadow-2xs hover:border-[#887055] transition-colors">
              <div className="w-12 h-12 rounded-xl bg-[#EADFCF] text-[#6B5640] flex items-center justify-center text-xl">
                <i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true"></i>
              </div>
              <h3 className="text-xl font-bold font-heading text-[#2C2523]">
                Hovedrengøring
              </h3>
              <p className="text-xs sm:text-sm text-[#5C524C] leading-relaxed">
                En ekstra grundig forårs- eller sæsonrengøring fra kælder til kvist. Vi kommer helt ud i krogene, bag møbler, i ovnen og afkalker badeværelset i dybden.
              </p>
              <ul className="text-xs text-[#2C2523] space-y-2 pt-2 border-t border-[#EADFCF]">
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#887055]"></i>
                  <span>Dybdegående og grundig</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#887055]"></i>
                  <span>Skræddersyet til dine fokusområder</span>
                </li>
              </ul>
            </div>

            <div className="bg-[#FFFDF9] rounded-2xl border border-[#EADFCF] p-6 space-y-4 shadow-2xs hover:border-[#887055] transition-colors">
              <div className="w-12 h-12 rounded-xl bg-[#EADFCF] text-[#6B5640] flex items-center justify-center text-xl">
                <i className="fa-solid fa-truck-ramp-box" aria-hidden="true"></i>
              </div>
              <h3 className="text-xl font-bold font-heading text-[#2C2523]">
                Flytterengøring
              </h3>
              <p className="text-xs sm:text-sm text-[#5C524C] leading-relaxed">
                Komplet slutrengøring ved fraflytning eller indflytning i hus eller lejlighed. Vi sikrer, at boligen fremstår upåklagelig ved overdragelse eller syn.
              </p>
              <ul className="text-xs text-[#2C2523] space-y-2 pt-2 border-t border-[#EADFCF]">
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#887055]"></i>
                  <span>Fuld afleveringsgaranti</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#887055]"></i>
                  <span>Køkken, bad, skabe og gulve</span>
                </li>
              </ul>
            </div>

          </div>

          {/* Included checklist */}
          <div className="bg-[#F4EFE6] rounded-3xl p-6 sm:p-10 border border-[#EADFCF] space-y-6">
            <h3 className="text-xl font-bold font-heading text-[#2C2523] flex items-center gap-2.5">
              <i className="fa-solid fa-list-check text-[#887055]" aria-hidden="true"></i>
              <span>Hvad indeholder en standard rengøring af hus eller lejlighed?</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-[#3A302B]">
              <div className="flex items-start gap-2.5 bg-[#FAF7F2] p-4 rounded-xl border border-[#EADFCF]">
                <i className="fa-solid fa-check text-[#887055] mt-1 shrink-0" aria-hidden="true"></i>
                <span>Støvsugning af alle gulve, tæpper og tilgængelige paneler</span>
              </div>
              <div className="flex items-start gap-2.5 bg-[#FAF7F2] p-4 rounded-xl border border-[#EADFCF]">
                <i className="fa-solid fa-check text-[#887055] mt-1 shrink-0" aria-hidden="true"></i>
                <span>Vask af hårde gulve med skånsomme, miljøvenlige produkter</span>
              </div>
              <div className="flex items-start gap-2.5 bg-[#FAF7F2] p-4 rounded-xl border border-[#EADFCF]">
                <i className="fa-solid fa-check text-[#887055] mt-1 shrink-0" aria-hidden="true"></i>
                <span>Aftørring af alle frie overflader, borde, vindueskarme og reoler</span>
              </div>
              <div className="flex items-start gap-2.5 bg-[#FAF7F2] p-4 rounded-xl border border-[#EADFCF]">
                <i className="fa-solid fa-check text-[#887055] mt-1 shrink-0" aria-hidden="true"></i>
                <span>Køkkenrengøring: vask, armatur, komfur, bordplader og udvendig aftørring</span>
              </div>
              <div className="flex items-start gap-2.5 bg-[#FAF7F2] p-4 rounded-xl border border-[#EADFCF]">
                <i className="fa-solid fa-check text-[#887055] mt-1 shrink-0" aria-hidden="true"></i>
                <span>Badeværelse: afkalkning og rengøring af toilet, håndvask, spejl og bruseniche</span>
              </div>
              <div className="flex items-start gap-2.5 bg-[#FAF7F2] p-4 rounded-xl border border-[#EADFCF]">
                <i className="fa-solid fa-check text-[#887055] mt-1 shrink-0" aria-hidden="true"></i>
                <span>Tømning af affaldsspande og let oprydning</span>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* PRICING INFO SECTION */}
      <section id="priser" className="py-14 lg:py-20 bg-[#F4EFE6] border-y border-[#EADFCF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#FFFDF9] rounded-3xl p-8 sm:p-12 border border-[#EADFCF] shadow-sm max-w-4xl mx-auto text-center space-y-6">
            <div className="w-16 h-16 rounded-2xl bg-[#EADFCF] text-[#6B5640] flex items-center justify-center text-2xl mx-auto">
              <i className="fa-solid fa-coins" aria-hidden="true"></i>
            </div>

            <div className="space-y-2">
              <span className="text-xs font-bold uppercase tracking-wider text-[#887055]">
                Priser & Aftaler
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold font-heading text-[#2C2523]">
                Priser for rengøring af hus eller lejlighed
              </h2>
            </div>

            <p className="text-sm sm:text-base text-[#5C524C] leading-relaxed max-w-2xl mx-auto">
              Priserne for rengøring af private huse og lejligheder offentliggøres her på siden snarest. Vi tilbyder både faste abonnementsaftaler og enkeltstående rengøringer. Kontakt os gerne med dine ønsker og boligens areal, så vender vi tilbage med en skarp og gennemskuelig pris.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 text-xs sm:text-sm text-[#2C2523] max-w-2xl mx-auto text-left">
              <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#EADFCF] flex items-center gap-3">
                <i className="fa-solid fa-calendar-days text-[#887055] text-lg shrink-0" aria-hidden="true"></i>
                <span className="font-medium">Mandag til fredag mellem kl. 9 og 15</span>
              </div>
              <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#EADFCF] flex items-center gap-3">
                <i className="fa-solid fa-file-signature text-[#887055] text-lg shrink-0" aria-hidden="true"></i>
                <span className="font-medium">Klar skriftlig aftale for hver opgave</span>
              </div>
              <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#EADFCF] flex items-center gap-3">
                <i className="fa-solid fa-broom text-[#887055] text-lg shrink-0" aria-hidden="true"></i>
                <span className="font-medium">Vi medbringer alt udstyr og midler</span>
              </div>
            </div>

            <div className="pt-4 flex flex-wrap justify-center gap-4">
              <a
                href="#bestil-form"
                className="bg-[#3A302B] hover:bg-[#231C19] text-[#FFFDF9] px-6 py-3 rounded-xl font-semibold text-sm transition-colors flex items-center gap-2 shadow-xs"
              >
                <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true"></i>
                <span>Bestil rengøring eller indhent tilbud</span>
              </a>
              <a
                href={`tel:${BUSINESS_INFO.phone}`}
                className="bg-[#EADFCF] hover:bg-[#D8C7B0] text-[#2C2523] px-6 py-3 rounded-xl font-semibold text-sm transition-colors border border-[#D8C7B0] flex items-center gap-2"
              >
                <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
                <span>Ring 40 94 75 40</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCTS & ENVIRONMENT */}
      <section className="py-12 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#FFFDF9] p-6 sm:p-8 rounded-2xl border border-[#EADFCF] space-y-4">
            <div className="flex items-center gap-2.5 text-[#2C2523] font-bold font-heading">
              <i className="fa-solid fa-leaf text-[#887055]"></i>
              <span>Miljøvenlige kvalitetsprodukter</span>
            </div>
            <div className="text-sm text-[#4A403A] space-y-2">
              <p>{PRODUCTS_AND_EQUIPMENT.intro}</p>
              <p className="text-xs sm:text-sm text-[#5C524C]">{PRODUCTS_AND_EQUIPMENT.subIntro}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ORDER / INQUIRY FORM */}
      <section id="bestil-form" className="py-16 lg:py-24 bg-[#FAF7F2] border-t border-[#EADFCF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <GeneralContactForm defaultService="Rengøring af hus eller lejlighed" />
        </div>
      </section>
    </>
  );
}
